import { PLAYER, POWERUP, SPEED, WORLD } from "../constants";
import { createPlayer, isInvincible, takeHit, updatePlayer } from "../entities/Player";
import { createProjectile, updateProjectile } from "../entities/Projectile";
import { Renderer } from "../Renderer";
import { intersects } from "../systems/collision";
import { createScoring, rewardDestroy, rewardDodge, tickScoring } from "../systems/scoring";
import { createSpawner, tickSpawner } from "../systems/spawner";
import { InputController } from "../input";
import { mulberry32, randomSeed, type Rng } from "./prng";
import type { EngineEvents, HudSnapshot, LevelConfig, Obstacle, PlayerState, PowerUp, Projectile } from "./types";

export interface EngineOptions {
  canvas: HTMLCanvasElement;
  input: InputController;
  events?: EngineEvents;
  /** When omitted, a random local seed is used (single-player). */
  levelConfig?: Partial<LevelConfig> & { seed?: number };
}

const STEP_SEC = 1 / 120; // fixed-update slice
const MAX_FRAME_SEC = 0.25; // clamp big spikes (tab switches, etc.)

/**
 * Core game loop. Owns world state and orchestrates per-frame:
 *   input -> simulate (fixed step) -> render -> hud event
 *
 * Decoupled from React; React mounts a canvas and bridges UI via callbacks.
 */
export class GameEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly renderer: Renderer;
  private readonly input: InputController;
  private readonly events: EngineEvents;
  private readonly rng: Rng;
  private readonly seed: number;

  private player: PlayerState = createPlayer();
  private obstacles: Obstacle[] = [];
  private powerups: PowerUp[] = [];
  private projectiles: Projectile[] = [];
  private scoring = createScoring();
  private spawner = createSpawner();
  private speed: number = SPEED.BASE;
  private elapsedSec = 0;
  private accumulator = 0;
  private lastFrameMs = 0;
  private rafId: number | null = null;
  private running = false;
  private paused = false;
  private gameOverFired = false;
  private hudCounter = 0;

  private readonly onResize = () => this.resize();
  private readonly onVisibility = () => {
    if (document.hidden && this.running && !this.paused) this.pause();
  };

  constructor(opts: EngineOptions) {
    this.canvas = opts.canvas;
    const ctx = opts.canvas.getContext("2d");
    if (!ctx) throw new Error("2D context unavailable");
    this.ctx = ctx;
    this.renderer = new Renderer(ctx);
    this.input = opts.input;
    this.events = opts.events ?? {};
    this.seed = opts.levelConfig?.seed ?? randomSeed();
    this.rng = mulberry32(this.seed);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.paused = false;
    this.gameOverFired = false;
    this.lastFrameMs = performance.now();
    window.addEventListener("resize", this.onResize);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.resize();
    this.emitHud();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.draw(performance.now());
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.lastFrameMs = performance.now();
  }

  togglePause(): void {
    this.paused ? this.resume() : this.pause();
  }

  isPaused(): boolean {
    return this.paused;
  }

  isAlive(): boolean {
    return this.player.alive;
  }

  getSeed(): number {
    return this.seed;
  }

  getFinalScore(): number {
    return Math.floor(this.scoring.score);
  }

  // ── Loop ─────────────────────────────────────────────────────────────────
  private readonly frame = (now: number) => {
    if (!this.running) return;
    if (!this.paused) {
      const rawDt = (now - this.lastFrameMs) / 1000;
      const frameSec = Math.min(MAX_FRAME_SEC, Math.max(0, rawDt));
      this.lastFrameMs = now;
      this.accumulator += frameSec;
      while (this.accumulator >= STEP_SEC) {
        this.simulate(STEP_SEC, now);
        this.accumulator -= STEP_SEC;
      }
    } else {
      this.lastFrameMs = now;
    }
    this.draw(now);
    this.rafId = requestAnimationFrame(this.frame);
  };

  private simulate(dtSec: number, nowMs: number): void {
    if (!this.player.alive) return;

    const snap = this.input.consume();
    if (snap.pauseEdge) {
      this.togglePause();
      return;
    }

    // Speed ramp
    this.elapsedSec += dtSec;
    this.speed = Math.min(SPEED.MAX, SPEED.BASE + SPEED.RAMP_PER_SEC * this.elapsedSec);

    // Player update
    updatePlayer(
      this.player,
      { jumpPressed: snap.jumpEdge, duckHeld: snap.duckHeld },
      dtSec,
      nowMs
    );

    // Shield throw (with cooldown + charges)
    if (snap.throwEdge && this.canThrow(nowMs)) {
      this.projectiles.push(
        createProjectile(this.player.x + this.player.w, this.player.y + this.player.h / 2 - 15)
      );
      this.player.shieldCharges -= 1;
      this.player.shieldReadyAtMs = nowMs + PLAYER.SHIELD_COOLDOWN_MS;
    }

    this.renderer.updateParallax(this.speed, dtSec);

    // Move world entities leftward
    const dx = this.speed * dtSec;
    for (const o of this.obstacles) o.x -= dx;
    for (const p of this.powerups) p.x -= dx;
    for (const pr of this.projectiles) updateProjectile(pr, dtSec);

    // Spawn
    tickSpawner(this.spawner, this.rng, dtSec, this.speed, {
      obstacle: (o) => this.obstacles.push(o),
      powerup: (p) => this.powerups.push(p),
    });

    // Collisions
    this.handleObstacleHits(nowMs);
    this.handlePowerUpPickups(nowMs);
    this.handleProjectileImpacts();

    // Cull off-screen
    this.obstacles = this.obstacles.filter((o) => {
      if (o.x + o.w < -20) {
        if (o.alive) rewardDodge(this.scoring);
        return false;
      }
      return o.alive;
    });
    this.powerups = this.powerups.filter((p) => p.alive && p.x + p.w > -20);
    this.projectiles = this.projectiles.filter((pr) => pr.alive);

    // Scoring
    this.scoring.multiplierActive = nowMs < this.player.multiplierUntilMs;
    tickScoring(this.scoring, dtSec);

    if (!this.player.alive && !this.gameOverFired) {
      this.gameOverFired = true;
      this.events.onGameOver?.(this.getFinalScore());
    }

    // HUD updates ~10/s — cheaper than every frame.
    this.hudCounter += dtSec;
    if (this.hudCounter >= 0.1) {
      this.hudCounter = 0;
      this.emitHud();
    }
  }

  private canThrow(nowMs: number): boolean {
    return this.player.shieldCharges > 0 && nowMs >= this.player.shieldReadyAtMs;
  }

  private handleObstacleHits(nowMs: number): void {
    for (const o of this.obstacles) {
      if (!o.alive) continue;
      if (!intersects(this.player, o)) continue;
      if (isInvincible(this.player, nowMs)) {
        // Plough through obstacles during invincibility.
        o.alive = false;
        rewardDestroy(this.scoring);
        continue;
      }
      const tookDamage = takeHit(this.player, nowMs);
      if (tookDamage) o.alive = false;
    }
  }

  private handlePowerUpPickups(nowMs: number): void {
    for (const p of this.powerups) {
      if (!p.alive) continue;
      if (!intersects(this.player, p)) continue;
      p.alive = false;
      switch (p.kind) {
        case "SHIELD":
          this.player.invincibleUntilMs = Math.max(
            this.player.invincibleUntilMs,
            nowMs + POWERUP.DURATION_MS
          );
          this.player.shieldCharges = Math.min(
            PLAYER.SHIELD_CHARGES + 2,
            this.player.shieldCharges + 1
          );
          break;
        case "MULTIPLIER":
          this.player.multiplierUntilMs = Math.max(
            this.player.multiplierUntilMs,
            nowMs + POWERUP.DURATION_MS
          );
          break;
        case "LIFE":
          this.player.lives = Math.min(PLAYER.LIVES + 2, this.player.lives + 1);
          break;
      }
    }
  }

  private handleProjectileImpacts(): void {
    for (const pr of this.projectiles) {
      if (!pr.alive) continue;
      for (const o of this.obstacles) {
        if (!o.alive) continue;
        if (intersects(pr, o)) {
          o.alive = false;
          pr.alive = false;
          rewardDestroy(this.scoring);
          break;
        }
      }
    }
  }

  // ── Rendering & sizing ──────────────────────────────────────────────────
  private draw(nowMs: number): void {
    this.renderer.drawWorld({
      player: this.player,
      obstacles: this.obstacles,
      powerups: this.powerups,
      projectiles: this.projectiles,
      nowMs,
      paused: this.paused,
    });
  }

  private resize(): void {
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const cssAspect = WORLD.WIDTH / WORLD.HEIGHT;
    const containerAspect = rect.width / rect.height;
    let drawW = rect.width;
    let drawH = rect.height;
    if (containerAspect > cssAspect) drawW = rect.height * cssAspect;
    else drawH = rect.width / cssAspect;

    // Internal pixel buffer matches the WORLD coords scaled by DPR.
    this.canvas.width = Math.floor(WORLD.WIDTH * dpr);
    this.canvas.height = Math.floor(WORLD.HEIGHT * dpr);
    // CSS size keeps aspect (centered via parent flex/grid).
    this.canvas.style.width = `${Math.floor(drawW)}px`;
    this.canvas.style.height = `${Math.floor(drawH)}px`;

    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = true;
  }

  private emitHud(): void {
    const now = performance.now();
    const hud: HudSnapshot = {
      score: Math.floor(this.scoring.score),
      lives: this.player.lives,
      combo: this.scoring.combo,
      shieldCharges: this.player.shieldCharges,
      multiplierActive: now < this.player.multiplierUntilMs,
      invincible: now < this.player.invincibleUntilMs,
      speed: this.speed,
      elapsedSec: this.elapsedSec,
    };
    this.events.onHudUpdate?.(hud);
  }
}
